import React, { useCallback, useMemo } from 'react'

import { Button } from '@refera/ui-web'
import { navigate, useLocation, useParams } from '@reach/router'
import useRolePermission from '_/hooks/use-role-permission'
import { parse } from 'query-string'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'
import {
  getStepStatusLog,
  updateBudget,
  updateBudgetResolution,
  getBudgetById,
  createBudget,
  addBudgetPicture,
  deleteBudgetPictures,
} from '_/modules/budget/actions'
import { useDispatch } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { ROUTES } from '_/utils/constants'
import { getServiceOrder } from '_/modules/service-orders/actions'
import { verifyIfBudgetIsSent } from '_/utils/verify-if-budget-is-sent'
import { BUTTONS_ACTION } from '_/utils/constants/service-order'
import { CanDoButtonAction } from '_/utils/can-do-button-action'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { useFormContext } from 'react-hook-form'
import {
  createFormDataBudgetResolution,
  calculateTotalValue,
  formatMaterialsPayload,
} from '_/views/budget-view-v2/utils/functions'

export function SendButton() {
  const dispatch = useDispatch()
  const { serviceOrderId, budgetId } = useParams()
  const { isTradesman, isSAAS, isAdmin, isAdminSAAS, checkIfUserDoesNotPermission } =
    useRolePermission()
  const { pathname, search } = useLocation()
  const { tipo: budgetType } = parse(search)
  const {
    isEditing,
    setIsEditing,
    setLoading,
    budget,
    setResetForm,
    fetchBudget,
    setBudgetResolutionToEdit,
  } = useBudgetForm()
  const { getValues, handleSubmit } = useFormContext()
  const { showToast } = useToast()

  const isCreating = pathname.includes('/novo')
  const isBudgetSent = verifyIfBudgetIsSent(budget)

  const canProviderEditBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_BUDGET_BY_PROVIDER,
  })

  const canSAASEditBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_BUDGET,
  })

  const canTemporarySave = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.TEMPORARY_SAVE,
  })

  const isValidSAASBudgetType =
    budgetType === 'externo' || (budget && budget?.proposeOpened === false)

  const showButton = useMemo(() => {
    return (
      (isEditing && Number(budgetId) === Number(budget?.id)) ||
      (isCreating && (isTradesman || (isSAAS && isValidSAASBudgetType)))
    )
  }, [isCreating, isTradesman, isSAAS, isValidSAASBudgetType, isEditing, budget, budgetId])

  const sanitizeResolution = useCallback(resolution => {
    return {
      ...(resolution.id !== undefined && { id: resolution.id }),

      problem: { label: resolution.problemText },

      warrantyMonths: resolution?.warrantyMonths ?? 0,
      isServiceMandatory: resolution.isServiceMandatory ?? false,
      problemDescription: resolution.problemDescription ?? '',
      serviceDescription: resolution.serviceDescription ?? '',
      problemMedia: resolution.problemMedia ?? [],

      materials:
        resolution.materials?.map(m => ({
          id: m.id,
          description: m.description,
          quantity: m.quantity,
          unitPrice: m.unitPrice,
          total: m.total,
        })) ?? [],

      singleServicePrice: resolution.singleServicePrice ?? 0,
      laborValue: resolution.laborValue ?? 0,
      numberOfProfessionals: resolution.numberOfProfessionals ?? 0,
      laborTimeHours: resolution.laborTimeHours ?? 0,
      total: resolution.total ?? 0,
    }
  }, [])

  function onSuccess() {
    const params = { budgetView: true, showOriginalBudget: false }
    dispatch(getBudgetById(budgetId, params)).then(() => {
      setIsEditing(false)
    })
  }

  const updateBudgetResolutions = useCallback(
    async (budgetResolutions, currentBudget) => {
      try {
        const promises = budgetResolutions.map(resolution => {
          const clean = sanitizeResolution(resolution)

          const payload = {
            ...clean,
            materials: formatMaterialsPayload(clean.materials),
            total: calculateTotalValue(clean),
          }
          const formData = createFormDataBudgetResolution(payload)

          return dispatch(updateBudgetResolution(currentBudget, formData))
        })

        await Promise.all(promises).then(onSuccess)
      } catch (error) {
        showToast({
          type: 'error',
          message: formatErrorMessage(error),
        })
        setLoading(false)
        throw error
      }
    },
    [dispatch, budgetId, showToast, setLoading]
  )

  const handleCreateBudget = useCallback(
    async payload => {
      const isSAASCreatingExternalBudget = isSAAS && isCreating && budgetType === 'externo'

      if (!canTemporarySave && !isAdmin) {
        showToast({
          message: 'Este chamado está em uma etapa que não permite incluir orçamento.',
          type: 'error',
        })
        return null
      }

      if (!isAdminSAAS) {
        const unauthorized = checkIfUserDoesNotPermission(
          PERMISSIONS.ORIGINAL_BUDGETS,
          PERMISSIONS_ACTIONS.ADD
        )
        if (unauthorized) return null
      }
      if (!isSAASCreatingExternalBudget && !isTradesman) {
        return null
      }

      const finalPayload = {
        ...payload,
        serviceOrder: serviceOrderId,
      }

      try {
        const newBudget = await dispatch(createBudget(finalPayload))

        return newBudget?.id
      } catch (error) {
        showToast({ type: 'error', message: formatErrorMessage(error) })
        setLoading(false)
        throw error
      }
    },
    [
      budgetType,
      isSAAS,
      isCreating,
      canTemporarySave,
      isAdmin,
      isAdminSAAS,
      isTradesman,
      serviceOrderId,
      dispatch,
      showToast,
      setLoading,
    ]
  )

  const handleSave = useCallback(async () => {
    setLoading(true)
    const payload = getValues()

    const { budgetResolutions, budgetPictures, budgetPicturesToDelete, ...payloadToSend } = payload
    let currentBudget = budgetId

    if (!budgetId) {
      currentBudget = await handleCreateBudget(payloadToSend)
      if (!currentBudget) return
    }

    if (budgetId && ((isTradesman && !canProviderEditBudget) || (isSAAS && !canSAASEditBudget))) {
      setLoading(false)
      showToast({
        type: 'error',
        message: 'Este chamado está em uma etapa que não permite incluir orçamento.',
      })
      return
    }

    const options = { transformPayload: true, transformFormData: true, formData: true }

    if (!isBudgetSent) {
      payloadToSend.released = 'true'
    }

    payloadToSend.newBudgetResolutionsOrder = budgetResolutions.map(({ id }) => id).join(',')
    payloadToSend.visitHappenedTemp = String(payloadToSend.visitHappenedTemp)
    payloadToSend.isSimplified = true

    if (budgetResolutions?.length > 0) {
      try {
        await updateBudgetResolutions(budgetResolutions, currentBudget)
      } catch (error) {
        return
      }
    }

    await dispatch(updateBudget(serviceOrderId, currentBudget, payloadToSend, options))
      .then(async newBudget => {
        const budgetIdForAttachments = newBudget?.id ?? currentBudget

        const newImages = (budgetPictures || []).filter(f => f instanceof File)
        await Promise.all(
          newImages.map(picture =>
            dispatch(addBudgetPicture(serviceOrderId, budgetIdForAttachments, picture))
          )
        )

        if (budgetPicturesToDelete?.length) {
          await dispatch(
            deleteBudgetPictures(serviceOrderId, budgetIdForAttachments, {
              ids: budgetPicturesToDelete,
            })
          )
        }

        // redirecting to clone budget
        const params = { budgetView: true, showOriginalBudget: false }
        await dispatch(getBudgetById(newBudget?.id, params))
        setIsEditing(false)
        setBudgetResolutionToEdit(null)
        setResetForm(true)
        await Promise.all([
          dispatch(getServiceOrder(serviceOrderId)),
          dispatch(getStepStatusLog(serviceOrderId)),
        ])
        showToast({ type: 'success' })
        // to remove edit parameter from url

        if (newBudget?.id && !isBudgetSent) {
          navigate(
            `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamento-simplificado/${newBudget?.id}`
          )
        }
      })
      .catch(error =>
        showToast({
          message: formatErrorMessage(error),
          type: 'error',
        })
      )
      .finally(() => setLoading(false))
  }, [
    serviceOrderId,
    budgetId,
    dispatch,
    setLoading,
    showToast,
    budget,
    setResetForm,
    fetchBudget,
    setIsEditing,
    isBudgetSent,
    canProviderEditBudget,
    canSAASEditBudget,
    isTradesman,
    isSAAS,
    getValues,
    handleSubmit,
    updateBudgetResolutions,
    handleCreateBudget,
  ])

  const label = isBudgetSent ? 'Salvar' : 'Enviar'

  function handleValidationError() {
    showToast({
      type: 'error',
      message: 'Um ou mais dados do formulário possuem problemas.',
    })
  }

  return (
    showButton && (
      <Button
        onClick={handleSubmit(handleSave, handleValidationError)}
        color="primary"
        variant="primary"
      >
        {label}
      </Button>
    )
  )
}
